import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatRand, resolveImageUrl } from "../../lib/api";

const STATUSES = ["pending_payment", "paid", "processing", "shipped", "completed", "cancelled"];
const CONFIRMED_STATUSES = new Set(["paid", "processing", "shipped", "completed"]);

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [active, setActive] = useState(null);

  const fetchAll = () => api.get("/admin/orders").then((r) => setOrders(r.data.filter((o) => CONFIRMED_STATUSES.has(o.status))));

  useEffect(() => {
    fetchAll();
  }, []);

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}/status`, { status });
      toast.success(`Order marked as ${status.replace("_", " ")}`);
      fetchAll();
      if (active && active.id === orderId) {
        setActive((a) => ({ ...a, status }));
      }
    } catch (err) {
      toast.error("Could not update status");
    }
  };

  const removeOrder = async (orderId) => {
    if (!confirm("Delete this order permanently?")) return;
    try {
      await api.delete(`/admin/orders/${orderId}`);
      toast.success("Order deleted");
      if (active && active.id === orderId) setActive(null);
      fetchAll();
    } catch (err) {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="border-b hairline pb-6 mb-8">
        <div className="overline mb-2">Operations</div>
        <h1 className="font-display text-4xl uppercase tracking-tighter">Orders</h1>
      </div>
      <div className="border hairline overflow-x-auto" style={{ background: "#fff" }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Order</th><th>Customer</th><th>Total</th><th>Status</th><th>Date</th><th></th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr><td colSpan={6} className="text-black/40 text-center py-8">No orders</td></tr>
            ) : orders.map((o) => (
              <tr key={o.id} data-testid={`admin-order-row-${o.order_number}`}>
                <td className="font-display">{o.order_number}</td>
                <td>{o.customer_name}<br/><span className="text-xs text-black/55">{o.customer_email}</span></td>
                <td className="font-display">{formatRand(o.total)}</td>
                <td>
                  <select
                    className="input-box text-xs"
                    value={o.status}
                    onChange={(e) => updateStatus(o.id, e.target.value)}
                    data-testid={`admin-order-status-${o.order_number}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s.replace("_", " ")}</option>
                    ))}
                  </select>
                </td>
                <td className="text-xs text-black/55">{new Date(o.created_at).toLocaleString()}</td>
                <td>
                  <div className="flex gap-1 justify-end items-center">
                    <button onClick={() => setActive(o)} className="btn-ghost" data-testid={`admin-view-order-${o.order_number}`}>View →</button>
                    <button onClick={() => removeOrder(o.id)} className="p-2 hover:bg-black/5" data-testid={`admin-delete-order-${o.order_number}`} title="Delete order"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {active && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="admin-order-dialog">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="overline mb-1">Order</div>
                <h2 className="font-display text-3xl uppercase tracking-tighter">{active.order_number}</h2>
              </div>
              <button onClick={() => setActive(null)} className="btn-ghost" data-testid="admin-order-close">Close</button>
            </div>
            <div className="grid gap-2 text-sm mb-6">
              <div><span className="overline text-black/55 mr-2">Customer</span>{active.customer_name} · {active.customer_email}</div>
              <div><span className="overline text-black/55 mr-2">Phone</span>{active.customer_phone || "—"}</div>
              <div><span className="overline text-black/55 mr-2">Address</span>{active.shipping?.line1}, {active.shipping?.city}, {active.shipping?.province}, {active.shipping?.postal_code}, {active.shipping?.country}</div>
              <div><span className="overline text-black/55 mr-2">Status</span><span className="status-pill muted">{active.status.replace("_", " ")}</span></div>
              {active.notes && <div><span className="overline text-black/55 mr-2">Notes</span>{active.notes}</div>}
            </div>
            <ul className="border hairline" style={{ background: "var(--bg)" }}>
              {active.items.map((it, idx) => (
                <li key={idx} className="px-4 py-3 border-b hairline flex gap-3 items-center">
                  <div className="w-12 h-14" style={{ background: "var(--bg-alt)" }}>
                    <img src={resolveImageUrl(it.image)} alt={it.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <div className="font-display uppercase text-sm">{it.name}</div>
                    <div className="text-xs text-black/55">{[it.color, it.size].filter(Boolean).join(" · ")} · Qty {it.quantity}</div>
                  </div>
                  <div className="font-display">{formatRand(it.price * it.quantity)}</div>
                </li>
              ))}
            </ul>
            <div className="flex justify-between items-center mt-6 border-t hairline pt-4">
              <span className="overline">Total</span>
              <span className="font-display text-2xl">{formatRand(active.total)}</span>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {STATUSES.filter((s) => s !== active.status).map((s) => (
                <button key={s} onClick={() => updateStatus(active.id, s)} className="status-pill hover:bg-black hover:text-white" data-testid={`admin-set-status-${s}`}>
                  Mark {s.replace("_", " ")}
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs text-black/55">Marking as paid (or beyond) triggers the member welcome email if the customer isn't a member yet.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
